import { BUILT_PROJECT_FOLDER, parseJson } from '@gnsx/genesys.js';

import type {
  FileChangeEvent,
  FileDownloadOptions,
  FileExistsOptions,
  IStorageProvider,
  StorageProviderType
} from './interfaces';
import type { AssetPath } from '@gnsx/genesys.js';
import type { FileListResult, FileUploadOptions } from '@gnsx/genesys.js';

import { TEXT_ASSET_EXTENSIONS } from '@/const';


export abstract class AbstractProvider implements IStorageProvider {
  async downloadFileAsText(path: AssetPath): Promise<string> {
    const content = await this.downloadFile(path);
    if (typeof content === 'string') {
      return content;
    }
    throw new Error(`content of ${path} is not a string, it's a ${typeof content}`);
  }

  async downloadFileAsBuffer(path: AssetPath): Promise<ArrayBuffer> {
    const content = await this.downloadFile(path) as string | ArrayBuffer;

    if (typeof content === 'string') {
      // Convert string to ArrayBuffer using TextEncoder
      const encoder = new TextEncoder();
      return encoder.encode(content).buffer as ArrayBuffer;
    }

    return content;
  }

  async downloadFileAsJson<T>(path: AssetPath): Promise<T> {
    const content = await this.downloadFileAsText(path);
    try {
      return parseJson(content) as T;
    } catch (error) {
      throw new Error(`${path} is not a JSON object: ${error}`);
    }
  }

  protected async extractTextContent(response: Response, path: string): Promise<string | null> {
    const contentType = response.headers.get('Content-Type');
    const fileExtension = path.split('.').pop()?.toLowerCase() ?? '';

    // List of extensions we know should be treated as text
    const textExtensions = [
      'txt',
      'json',
      'ts',
      'tsx',
      'js',
      'jsx',
      'md',
      'css',
      'html',
      'htm',
      'xml',
      'svg',
      'csv',
      'meta',
      ...TEXT_ASSET_EXTENSIONS.map(ext => ext.slice(1)) // Remove leading dot from extensions
    ];

    // Check if content type indicates text OR if file extension suggests it's text
    const isTextByContentType = contentType && (
      contentType.includes('text') ||
      contentType.includes('application/json') ||
      contentType.includes('application/javascript') ||
      contentType.includes('application/typescript')
    );

    const isTextByExtension = textExtensions.includes(fileExtension);

    // If either content type or extension suggests it's text, try to get it as text
    if (isTextByContentType || isTextByExtension) {
      return await response.text();
    }
    return null;
  }

  public async buildCurrentProject(): Promise<boolean> {
    throw new Error('Not supported');
  }

  public async listFiles(path: AssetPath, recursive: boolean, includeHiddenFiles: boolean): Promise<FileListResult> {
    const files = await this.doListFiles(path, recursive);
    if (!includeHiddenFiles) {
      files.files = files.files.filter(file => !file.path.includes(BUILT_PROJECT_FOLDER));
      files.directories = files.directories.filter(dir => !dir.path.includes(BUILT_PROJECT_FOLDER));
    }
    return files;
  }

  //==============================================================================

  protected async exec(command: string): Promise<boolean> {
    throw new Error('Not supported');
  }

  abstract doListFiles(path: AssetPath, recursive: boolean): Promise<FileListResult>;
  abstract uploadFile(
    path: AssetPath,
    content: Blob | File | string | ArrayBuffer,
    options?: FileUploadOptions
  ): Promise<{ path: string; name: string }>;
  abstract downloadFile(
    path: AssetPath,
    options?: FileDownloadOptions
  ): Promise<Blob | string | ArrayBuffer>;
  abstract deleteItem(path: AssetPath, recursive?: boolean): Promise<void>;
  abstract createDirectory(path: AssetPath): Promise<void>;
  abstract exists(path: AssetPath, options?: FileExistsOptions): Promise<boolean>;
  abstract resolvePath(path: AssetPath, expiry?: number): Promise<AssetPath>;
  abstract providerType: StorageProviderType;
  abstract onFileSystemChange(callback: (event: FileChangeEvent) => void): () => void;
  abstract requireProjectContext(): boolean;
}
